import {NavLink, useLocation} from 'react-router-dom';
import {Badge, makeStyles, tokens} from '@fluentui/react-components';
import {useQuery} from '@tanstack/react-query';
import {getApiV1DocumentsUnreadCount} from '@api/endpoints/documents';
import {
    Home24Regular,
    Calendar24Regular,
    ContactCard24Regular, Form24Regular, Document24Regular, Backpack24Regular, PersonAdd24Regular, People24Regular,
    Building24Regular, PersonAvailable24Regular, History24Regular, Shield24Regular, ClipboardCheckmark24Regular,
    DocumentArrowDown24Regular,
} from '@fluentui/react-icons';
import {useTranslation} from 'react-i18next';

const useStyles = makeStyles({
    nav: {
        display: 'flex',
        flexDirection: 'column',
        padding: tokens.spacingVerticalM,
        gap: tokens.spacingVerticalXS,
    },
    section: {
        marginBottom: tokens.spacingVerticalM,
    },
    link: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        borderRadius: tokens.borderRadiusMedium,
        textDecoration: 'none',
        color: 'inherit',
        fontSize: tokens.fontSizeBase300,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    linkActive: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
        fontWeight: tokens.fontWeightSemibold,
        '&:hover': {
            backgroundColor: tokens.colorBrandBackground2Hover,
        },
    },
    icon: {
        fontSize: '20px',
        flexShrink: 0,
    },
    navLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacingHorizontalS,
        width: '100%',
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground3,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    },
});

interface NavItemProps {
    to: string;
    icon: React.ReactNode;
    label: string;
}

function NavItem({to, icon, label}: NavItemProps) {
    const styles = useStyles();
    const location = useLocation();
    const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);

    return (
        <NavLink
            to={to}
            className={`${styles.link} ${isActive ? styles.linkActive : ''}`}
        >
            <span className={styles.icon}>{icon}</span>
            <span>{label}</span>
        </NavLink>
    );
}

export function AdminNavMenu() {
    const styles = useStyles();
    const {t} = useTranslation('navigation');
    const unreadDocumentsQuery = useQuery({
        queryKey: ['documents', 'unread-count'],
        queryFn: () => getApiV1DocumentsUnreadCount(),
    });
    const unreadDocuments = Number(unreadDocumentsQuery.data?.count ?? 0);
    
    return (
        <nav className={styles.nav}>
            <div className={styles.section}>
                <NavItem to="/" icon={<Home24Regular/>} label={t('dashboard')}/>
                <NavItem to="/visitors" icon={<Calendar24Regular/>} label={t('appointments')}/>
                <NavItem to="/contacts" icon={<ContactCard24Regular/>} label={t('contacts')}/>
            </div>
            <div className={styles.section}>
                <NavLink to="/visitor-registration" className={styles.link}>
                    <span className={styles.icon}><Form24Regular/></span>
                    <span>{t('visitorRegistration')}</span>
                </NavLink>
                <NavLink to="/documents" className={styles.link}>
                    <span className={styles.icon}><Document24Regular/></span>
                    <span className={styles.navLabel}>
                            <span>{t('documents')}</span>
                        {unreadDocuments > 0 ? (
                            <Badge appearance="filled" color="danger">{unreadDocuments}</Badge>
                        ) : null}
                        </span>
                </NavLink>
            </div>
            <div className={styles.section}>
                <div className={styles.sectionTitle}>{t('employeeServices')}</div>
                <NavItem to="/employees" icon={<Backpack24Regular />} label={t('employees')} />
                <NavItem to="/external-employees" icon={<PersonAdd24Regular />} label={t('externalEmployees')} />
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>{t('checkIn')}</div>
                <NavItem to="/reception" icon={<People24Regular />} label={t('visitorCheckIn')} />
                <NavItem to="/reception-contractor" icon={<Building24Regular />} label={t('contractorCheckIn')} />
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>{t('keyManagement')}</div>
                <NavItem to="/issuance/key-issuance" icon={<PersonAvailable24Regular />} label={t('issuance')} />
                <NavItem to="/issuance/history" icon={<History24Regular />} label={t('itemHistory')} />
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>{t('wachbuch')}</div>
                <NavItem to="/logbook/incident" icon={<Shield24Regular />} label={t('vorfall')} />
                <NavItem to="/logbook/reports" icon={<ClipboardCheckmark24Regular />} label={t('meldungen')} />
                <NavItem to="/logbook/export" icon={<DocumentArrowDown24Regular />} label={t('export')} />
            </div>
        </nav>
    );
}
